import React from "react";
import type { SwimmerReportDataItem } from "../../../../api/client/types/swimmer.types";
import type { PaginatedResponse } from "../../../../api/client/types/common.types";
import { PaginatedTable } from "../../../../shared/components/ui/PaginatedTable";
import { EditButton } from "../../../../shared/components/ui/EditButton";
import { type ColumnDef } from "@tanstack/react-table";
import { pluralize } from "../../../../shared/utils/stringUtils";
import { Trash2 } from "lucide-react";
import {
  formatDateAndTime,
  formatDateOnly,
} from "../../../../shared/utils/dateTimeFormat";

interface SwimmerPreviewTableProps {
  swimmersData: PaginatedResponse<SwimmerReportDataItem>;
  isLoading?: boolean;
  isError?: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Callback when the user clicks the edit icon */
  onEditSwimmer?: (item: SwimmerReportDataItem) => void;
  /** Callback when the user clicks the delete icon */
  onDeleteSwimmer?: (swimmerId: string) => void;
  /** When true, disables the delete button to prevent double submits */
  isDeletingSwimmer?: boolean;
  /** When true, draws borders around every table cell */
  showCellBorders?: boolean;
}

export const SwimmerPreviewTable: React.FC<SwimmerPreviewTableProps> = ({
  swimmersData,
  isLoading,
  isError,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEditSwimmer,
  onDeleteSwimmer,
  isDeletingSwimmer,
  showCellBorders,
}) => {
  const handleEditClick = React.useCallback(
    (item: SwimmerReportDataItem) => {
      onEditSwimmer?.(item);
    },
    [onEditSwimmer],
  );

  const columns = React.useMemo<ColumnDef<SwimmerReportDataItem>[]>(
    () => [
      {
        header: "ACCIONES",
        id: "actions",
        cell: ({ row }) => (
          <div className="flex items-center justify-center gap-2">
            <EditButton
              onClick={() => handleEditClick(row.original)}
              title="Editar bañista"
              iconSize={16}
            />
            <button
              onClick={() => onDeleteSwimmer?.(row.original.swimmer_id)}
              disabled={isDeletingSwimmer}
              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Eliminar bañista"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
      {
        header: () => (
          <span
            className="block bg-slate-300 text-slate-900 -mx-2.5 -my-2 px-2.5 py-2 min-h-full"
            title="Fecha de registro en el sistema (no se modifica al editar el bañista)"
          >
            CREADO EL
          </span>
        ),
        accessorKey: "created_at",
        cell: ({ getValue }) => {
          const { datePart, formattedTime } = formatDateAndTime(
            getValue() as string,
          );
          return (
            <div>
              <div className="whitespace-nowrap">{datePart}</div>
              {formattedTime && (
                <div className="whitespace-nowrap">{formattedTime}</div>
              )}
            </div>
          );
        },
      },
      {
        header: "FECHA",
        accessorKey: "registration_timestamp",
        cell: ({ getValue }) => {
          return (
            <div>
              <div className="whitespace-nowrap">
                {formatDateOnly(getValue() as string)}
              </div>
            </div>
          );
        },
      },
      {
        header: "UNIDAD",
        accessorKey: "building_name",
        cell: ({ getValue }) => (
          <div className="line-clamp-4" title={getValue() as string}>
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "SALVAVIDAS DE TURNO",
        accessorKey: "lifeguard_name",
        cell: ({ getValue }) => (
          <div className="line-clamp-4" title={getValue() as string}>
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "NOMBRE Y APELLIDO DEL BAÑISTA",
        accessorKey: "swimmer_name",
        cell: ({ getValue }) => (
          <div className="line-clamp-4" title={getValue() as string}>
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "TORRE",
        accessorKey: "building_tower",
        cell: ({ getValue }) => (
          <div className="whitespace-nowrap">{getValue() as string}</div>
        ),
      },
      {
        header: "APTO",
        accessorKey: "building_apartment",
        cell: ({ getValue }) => (
          <div className="whitespace-nowrap">{getValue() as string}</div>
        ),
      },
      {
        header: "TIPO DE USUARIO",
        accessorKey: "swimmer_type_name",
        cell: ({ getValue }) => (
          <div className="line-clamp-4" title={getValue() as string}>
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "EDAD",
        accessorKey: "age_classification_name",
        cell: ({ getValue }) => (
          <div className="line-clamp-4" title={getValue() as string}>
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "ADULTO RESPONSABLE",
        accessorKey: "responsible_name",
        cell: ({ getValue }) => (
          <div className="line-clamp-4" title={getValue() as string}>
            {getValue() as string}
          </div>
        ),
      },
    ],
    [handleEditClick, onDeleteSwimmer, isDeletingSwimmer],
  );

  return (
    <PaginatedTable<SwimmerReportDataItem>
      data={swimmersData}
      columns={columns}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      isLoading={isLoading}
      isError={isError}
      totalLabel="Total"
      entityName={pluralize(swimmersData?.total ?? 0, "bañista", "bañistas")}
      tableContainerClassName="overflow-x-auto border rounded-lg"
      showCellBorders={showCellBorders}
    />
  );
};
